// Account inspector drawer. Row click → right-hand drawer with freshness, KPIs, open
// positions, exposure, recent alerts and events. Table filters/sort/scroll are preserved
// underneath. Positions + events are fetched lazily for the selected account.

import { useEffect, useState } from 'react';
import * as api from '../api/client';
import { ApiError } from '../api/http';
import type { AccountRow } from '../types/api';
import { C, eventGlyph, MONO, SEV } from '../lib/tokens';
import { fmtAge, fmtBk, fmtMoney, fmtPctStr, fmtTS, hbTitle } from '../lib/format';
import { toNum } from '../lib/decimal';
import { chipS, mTd, mTh, mono, GBTN_SM } from '../lib/styles';
import { derive } from '../lib/mappers';
import { exposureBySymbol } from '../lib/exposure';
import { alertsForAccount } from '../lib/selectors';
import { pnlColor } from './common';
import { useAuth, useData, useServerNow, useUi } from '../store/hooks';
import { closeDrawer, openDetail, setLabelAlias, toastMsg, uiStore } from '../store/uiStore';
import { feed } from '../store/dataStore';
import { useResource } from '../hooks/useResource';

function priceStr(s: string | null | undefined, digits: number): string {
  const n = toNum(s);
  return n == null ? '—' : n.toFixed(digits);
}

export function Drawer() {
  const drawerId = useUi((s) => s.drawerId);
  const thr = useUi((s) => s.thr);
  const editLabel = useUi((s) => s.editLabel);
  const labelVal = useUi((s) => s.labelVal);
  const aliases = useUi((s) => s.labels);
  const account = useData((s) => (drawerId ? s.accountsById[drawerId] : undefined));
  const alerts = useData((s) => s.alerts);
  const feedStatus = useData((s) => s.feedStatus);
  const serverNow = useServerNow();
  const isAdmin = useAuth((s) => s.user?.role === 'admin');
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Reset the decommission confirm whenever a different account is inspected.
  useEffect(() => {
    setConfirmDel(false);
    setDeleting(false);
  }, [drawerId]);

  async function decommission(id: string): Promise<void> {
    if (deleting) return;
    setDeleting(true);
    try {
      const res = await api.decommissionAccount(id);
      toastMsg(`Account decommissioned — removed ${res.deleted.alerts} alert(s) and its history.`);
      closeDrawer();
      await feed.resnapshot();
    } catch (e) {
      toastMsg('Could not decommission account: ' + (e instanceof ApiError ? e.message : 'error'));
      setDeleting(false);
    }
  }

  const positions = useResource(
    drawerId ? (signal) => api.accountPositions(drawerId, signal) : null,
    [drawerId, account?.portfolio_seq],
  );
  const events = useResource(
    drawerId ? (signal) => api.accountEvents(drawerId, 8, signal) : null,
    [drawerId],
  );

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeDrawer();
    }
    if (drawerId) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerId]);

  if (!account) return null;
  const a: AccountRow = account;
  const d = derive(a, serverNow, thr);
  const label = aliases[a.id] ?? a.label;
  const tzo = a.broker_tz_offset;
  const posList = positions.data?.positions ?? [];
  const evList = events.data?.events ?? [];
  const frozen = d.conn === 'stale' || d.conn === 'offline' || d.bc === 'disconnected' || feedStatus !== 'live';

  const chips: Array<[typeof d.cm, string]> = [
    [d.cm, ''],
    [d.bm, 'broker'],
    [d.rm, 'risk'],
    [d.tm, ''],
    [d.qm, 'data'],
  ];

  const kpi = (k: string, v: string, color?: string) => (
    <div key={k} style={{ background: '#121B2C', border: '1px solid ' + C.ln2, borderRadius: 6, padding: '7px 10px' }}>
      <div style={{ fontSize: 9.5, color: C.dm, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>{k}</div>
      <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 600, marginTop: 2, color: color ?? C.tx, fontVariantNumeric: 'tabular-nums' }}>{v}</div>
    </div>
  );

  const freshRows: Array<[string, string, string]> = [
    ['Heartbeat', fmtAge(d.hbAgeMs) + ' ago', hbTitle(d.receivedAtMs, tzo)],
    ['Last market quote', d.tr === 'inactive' ? '☾ market closed · ' + fmtAge(d.qtAgeMs) : fmtAge(d.qtAgeMs) + ' ago', hbTitle(d.quoteAtMs, tzo)],
    ['Broker time', fmtBk(serverNow, tzo) + ' (GMT' + (tzo >= 0 ? '+' : '') + tzo + ')', 'Server ' + a.server],
    ['Local time', fmtTS(serverNow), 'Your browser time'],
    ['Data quality', d.qm[0] + ' ' + d.qm[2], d.dq === 'partial' ? 'History permission missing — margin & drawdown unavailable' : 'Collector snapshot completeness'],
    ['Versions', 'EA ' + a.ea_version + ' · build ' + a.terminal_build, 'Terminal & collector versions'],
  ];

  const expo = exposureBySymbol(posList);
  const acctAlerts = alertsForAccount(alerts, a.id).slice(0, 3);

  return (
    <>
      <div onClick={closeDrawer} style={{ position: 'fixed', inset: 0, background: 'rgba(5,9,17,.55)', zIndex: 40 }} />
      <aside
        role="dialog"
        aria-label="Account inspector"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 432,
          maxWidth: '94vw',
          background: '#0F1726',
          borderLeft: '1px solid ' + C.ln,
          zIndex: 41,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-24px 0 48px rgba(0,0,0,.5)',
        }}
      >
        <div style={{ padding: '13px 16px', borderBottom: '1px solid ' + C.ln2, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {editLabel ? (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  value={labelVal}
                  onChange={(e) => uiStore.set({ labelVal: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveAlias(a.id, labelVal);
                    else if (e.key === 'Escape') uiStore.set({ editLabel: false });
                  }}
                  aria-label="Account label"
                  style={{ height: 28, background: '#0A0F19', border: '1px solid ' + C.bl, borderRadius: 5, color: C.tx, fontSize: 13, fontWeight: 600, padding: '0 8px', width: 170 }}
                />
                <button onClick={() => saveAlias(a.id, labelVal)} style={{ height: 28, padding: '0 10px', background: C.bl, border: 'none', borderRadius: 5, color: '#0B1220', fontSize: 11, fontWeight: 600 }}>Save</button>
                <button onClick={() => uiStore.set({ editLabel: false })} style={{ height: 28, padding: '0 8px', background: 'none', border: '1px solid ' + C.ln, borderRadius: 5, color: C.dm, fontSize: 11 }}>Cancel</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{label}</div>
                <button
                  onClick={() => uiStore.set({ editLabel: true, labelVal: label })}
                  title="Rename — local alias only, the broker account is untouched"
                  aria-label="Rename account label"
                  style={{ background: 'none', border: 'none', color: C.dim2, fontSize: 10.5, padding: '2px 0' }}
                >
                  ✎ rename
                </button>
              </div>
            )}
            <div style={{ fontSize: 11, color: C.dm, fontFamily: MONO, marginTop: 2 }}>
              login {a.login_masked} · magic {a.magic} · {a.margin_mode}
            </div>
            <div style={{ fontSize: 11, color: C.dm, marginTop: 1 }}>
              {a.broker} — {a.server} · {a.vps_id} / {a.terminal_id}
              {a.dup_terminal ? ' (duplicate terminal)' : ''}
            </div>
          </div>
          <button onClick={closeDrawer} aria-label="Close inspector" style={{ background: 'none', border: '1px solid ' + C.ln, borderRadius: 6, color: C.dm, width: 26, height: 26, fontSize: 12 }}>✕</button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, padding: '10px 16px 0' }}>
          {chips.map(([token, prefix], i) => (
            <span key={i} style={chipS(token[1])}>
              {token[0]} {prefix ? prefix + ' ' : ''}
              {token[2]}
            </span>
          ))}
        </div>

        {frozen && (
          <div role="status" style={{ margin: '10px 16px 0', padding: '8px 10px', background: 'rgba(224,168,62,.1)', border: '1px solid rgba(224,168,62,.35)', borderRadius: 6, fontSize: 11, color: C.am, lineHeight: 1.5 }}>
            Data frozen — last update {fmtAge(d.qtAgeMs)} ago ({fmtTS(a.quote_at ? Date.parse(a.quote_at) : null)} local). Values shown are the last received.
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            {freshRows.map(([k, v, ti]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '4px 0', borderBottom: '1px solid #16203A' }}>
                <span style={{ fontSize: 11, color: C.dm }}>{k}</span>
                <span style={{ fontSize: 11, fontFamily: MONO, textAlign: 'right' }} title={ti}>{v}</span>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {kpi('Balance', fmtMoney(a.balance, a.currency), (toNum(a.balance) ?? 0) < 0 ? C.rd : undefined)}
            {kpi('Equity', fmtMoney(a.equity, a.currency), (toNum(a.equity) ?? 0) <= 0 ? C.rd : undefined)}
            {kpi('Floating P&L', fmtMoney(a.floating_pl, a.currency, true), pnlColor(d.fltNum))}
            {kpi('Daily P&L', fmtMoney(a.daily_pl, a.currency, true) + (d.dailyPctNum != null ? ' (' + fmtPctStr(a.daily_pct, true) + ')' : ''), pnlColor(d.dailyNum))}
            {kpi('Drawdown', fmtPctStr(a.drawdown_pct), d.ddNum != null && d.ddNum > thr.dC ? C.rd : d.ddNum != null && d.ddNum > thr.dW ? C.am : undefined)}
            {kpi('Margin level', d.mlvlNum == null ? 'Unavailable' : Math.round(d.mlvlNum) + '%', d.mlvlNum == null ? C.gy : d.mlvlNum < thr.mC ? C.rd : d.mlvlNum < thr.mW ? C.am : undefined)}
            {kpi('Free margin', fmtMoney(a.free_margin, a.currency))}
            {kpi('Open volume', a.positions_count + ' pos · ' + (toNum(a.open_lots) ?? 0).toFixed(2) + ' lots', a.positions_count > 0 ? C.bl : undefined)}
          </div>

          <div>
            <h3 style={{ margin: '0 0 6px', fontSize: 11, color: C.dm, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>Open positions</h3>
            {posList.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Symbol', 'Dir', 'Lots', 'Entry', 'Now', 'P&L'].map((h, i) => (
                      <th key={h} scope="col" style={mTh(i > 1 ? 'right' : 'left')}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {posList.map((p) => {
                    const pl = toNum(p.profit);
                    return (
                      <tr key={p.position_id}>
                        <td style={mTd('left', mono())} title={'ticket ' + p.ticket}>{p.symbol}</td>
                        <td style={mTd('left', mono(p.side === 'buy' ? C.bl : C.violet))} title={p.side === 'buy' ? 'long' : 'short'}>{p.side === 'buy' ? 'L' : 'S'}</td>
                        <td style={mTd('right', mono())}>{(toNum(p.volume) ?? 0).toFixed(2)}</td>
                        <td style={mTd('right', mono(C.b9))}>{priceStr(p.price_open, p.digits)}</td>
                        <td style={mTd('right', mono(C.b9))} title={'quote ' + fmtAge(d.qtAgeMs) + ' ago'}>{priceStr(p.price_current, p.digits)}</td>
                        <td style={mTd('right', mono(pnlColor(pl)))} title={'swap ' + fmtMoney(p.swap, a.currency, true)}>{fmtMoney(p.profit, a.currency, true)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div style={{ padding: 10, fontSize: 11.5, color: C.dm, textAlign: 'center', background: '#121B2C', borderRadius: 6 }}>
                {positions.loading ? 'Loading positions…' : d.tr === 'disabled' ? 'Trading disabled (investor / read-only access).' : d.tr === 'sync' ? 'Synchronizing with terminal…' : 'Flat — no open positions.'}
              </div>
            )}
          </div>

          {expo.length > 0 && (
            <div>
              <h3 style={{ margin: '0 0 6px', fontSize: 11, color: C.dm, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>Exposure by symbol</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {expo.map((e) => (
                  <div key={e.sym} style={{ display: 'grid', gridTemplateColumns: '62px 1fr 1fr', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 10.5, fontFamily: MONO, color: C.tx }}>{e.sym}</span>
                    <Bar frac={e.buyFrac} color={C.bl} label={(toNum(e.buyLots) ?? 0).toFixed(2) + ' L'} />
                    <Bar frac={e.sellFrac} color={C.violet} label={(toNum(e.sellLots) ?? 0).toFixed(2) + ' S'} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <h3 style={{ margin: '0 0 6px', fontSize: 11, color: C.dm, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>Recent alerts</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {acctAlerts.length === 0 && <div style={{ fontSize: 11, color: C.dm }}>No alerts for this account.</div>}
              {acctAlerts.map((al) => {
                const g = SEV[al.severity]!;
                return (
                  <div key={al.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <span style={{ fontFamily: MONO, color: g[1], fontSize: 11 }} aria-hidden="true">{g[0]}</span>
                    <span style={{ flex: 1, fontSize: 11.5 }}>
                      {al.title}
                      <span style={{ display: 'block', fontSize: 10, color: C.dm }}>
                        {al.state} · started {fmtTS(Date.parse(al.started_at))} · {fmtAge(serverNow - Date.parse(al.started_at))} ago
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <h3 style={{ margin: '0 0 6px', fontSize: 11, color: C.dm, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>Recent events</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {evList.length === 0 && <div style={{ fontSize: 11, color: C.dm }}>{events.loading ? 'Loading…' : 'No recent events.'}</div>}
              {evList.map((e, i) => {
                const g = eventGlyph(e.kind);
                return (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                    <span style={{ fontFamily: MONO, color: g[1], fontSize: 11 }} aria-hidden="true">{g[0]}</span>
                    <span style={{ flex: 1, fontSize: 11.5 }}>
                      {e.label}
                      {e.amount ? ' — ' + fmtMoney(e.amount, a.currency, true) : ''}
                    </span>
                    <span style={{ fontSize: 10, color: C.dm, fontFamily: MONO }}>{fmtAge(serverNow - Date.parse(e.at))} ago</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div style={{ padding: '12px 16px', borderTop: '1px solid ' + C.ln2, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button onClick={() => openDetail(a.id)} style={{ width: '100%', padding: '9px 14px', background: C.bl, border: 'none', borderRadius: 6, color: '#0B1220', fontWeight: 600, fontSize: 12.5, fontFamily: 'inherit' }}>
            Open account detail →
          </button>
          {isAdmin &&
            (confirmDel ? (
              <div role="alert" style={{ display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid ' + C.rd + '55', background: 'rgba(228,91,91,.07)', borderRadius: 6, padding: '8px 10px' }}>
                <span style={{ fontSize: 11, color: C.am, lineHeight: 1.5 }}>
                  Decommission <strong>{label}</strong>? This removes the account and all its history and alerts. It re-registers if its terminal reports again.
                </span>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <button onClick={() => decommission(a.id)} disabled={deleting} style={{ ...GBTN_SM, color: C.rd, border: '1px solid ' + C.rd + '77', opacity: deleting ? 0.6 : 1 }}>
                    {deleting ? 'Deleting…' : 'Yes, decommission'}
                  </button>
                  <button onClick={() => setConfirmDel(false)} disabled={deleting} style={GBTN_SM}>Cancel</button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDel(true)}
                style={{ width: '100%', padding: '7px 14px', background: 'none', border: '1px solid ' + C.rd + '55', borderRadius: 6, color: C.rd, fontWeight: 600, fontSize: 11.5, fontFamily: 'inherit' }}
              >
                Decommission account
              </button>
            ))}
        </div>
      </aside>
    </>
  );
}

function Bar({ frac, color, label }: { frac: number; color: string; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ flex: 1, height: 9, background: '#16223A', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: Math.round(frac * 100) + '%', height: '100%', background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 9.5, fontFamily: MONO, color: C.dm, minWidth: 50, textAlign: 'right' }}>{label}</span>
    </div>
  );
}

function saveAlias(id: string, val: string) {
  const v = val.trim();
  if (!v) {
    uiStore.set({ editLabel: false });
    return;
  }
  setLabelAlias(id, v);
  toastMsg('Renamed to “' + v + '” — a local alias stored in this browser; the broker account is untouched.');
}
